import { supabase } from '@/lib/supabase';
import { mockPlans } from '@/mocks/plans';

export interface Plan {
  id: string;
  role: string;
  name: string;
  price_monthly: number;
  max_listings: number;
  max_products: number;
  storage_mb: number;
  modules: string[];
  description: string | null;
  sort_order: number;
}

export interface Subscription {
  id: string;
  user_id: string;
  plan_id: string;
  status: string;
  start_date: string;
  end_date: string | null;
  created_at: string;
}

export interface RoleMeta {
  label: string;
  icon: string;
  postLabel: string;
}

export const ROLE_META: Record<string, RoleMeta> = {
  business: { label: 'Entreprise', icon: 'ri-store-2-line', postLabel: 'annonces' },
  freelancer: { label: 'Freelance', icon: 'ri-user-star-line', postLabel: 'services' },
  instructor: { label: 'Formateur', icon: 'ri-graduation-cap-line', postLabel: 'formations' },
  user: { label: 'Particulier', icon: 'ri-user-3-line', postLabel: 'annonces' },
};

export const ROLE_ORDER = ['business', 'freelancer', 'user', 'instructor'];

export function formatPrice(cfa: number): string {
  return `${Number(cfa).toLocaleString('fr-FR')} FCFA`;
}

export function formatStorage(mb: number): string {
  if (mb >= 1024) {
    return `${mb / 1024} Go`;
  }
  return `${mb} Mo`;
}

export async function getPlans(): Promise<Plan[]> {
  const { data, error } = await supabase
    .from('plans')
    .select('*')
    .order('sort_order', { ascending: true });
  if (error || !data || data.length === 0) {
    return mockPlans as Plan[];
  }
  return data as Plan[];
}

export async function getMySubscription(
  userId: string
): Promise<{ subscription: Subscription | null; plan: Plan | null }> {
  const { data, error } = await supabase
    .from('subscriptions')
    .select('*')
    .eq('user_id', userId)
    .eq('status', 'active')
    .maybeSingle();

  if (error || !data) return { subscription: null, plan: null };

  const subscription = data as Subscription;
  const { data: planData } = await supabase
    .from('plans')
    .select('*')
    .eq('id', subscription.plan_id)
    .maybeSingle();

  return { subscription, plan: (planData as Plan | null) ?? null };
}

export async function subscribeToPlan(planId: string): Promise<Subscription> {
  const { data, error } = await supabase.rpc('subscribe_to_plan', { p_plan_id: planId });
  if (error) throw error;
  return data as Subscription;
}